import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faExclamationCircle,
  faHourglass,
  faTimes,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Timer } from '@sorare/core/src/atoms/icons/Timer';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelM } from '@sorare/core/src/atoms/typography';
import { findLastIndex } from '@sorare/core/src/lib/arrays';
import { getRgbColorFromHex } from '@sorare/core/src/lib/colors';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { rewardsOverviewToSimpleRewards } from '@sorare/core/src/lib/rewards';
import { HexColor } from '@sorare/core/src/style/types';

import { TargetIcon } from 'assets/icons/TargetIcon';
import { DumbStatus } from 'components/managerTeam/Status/DumbStatus';
import { messages } from 'components/managerTeam/Status/messages';
import { RewardOverview } from 'components/rewards/RewardOverview';
import { Variant } from 'components/rewards/RewardOverview/types';
import {
  ContenderStatus,
  useContenderStatus,
} from 'hooks/so5/useContenderStatus';
import { isFixtureOpened } from 'lib/so5';

import { LineupStatus_so5LeaderboardContender } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  min-height: 78px;
  &.isUpcoming {
    min-height: 48px;
  }
  justify-content: space-between;
  padding: var(--unit) var(--double-unit);

  > *:only-child {
    margin: auto 0;
  }

  background: linear-gradient(
    0deg,
    rgba(var(--rgb-gradient-color), 0.2) 0%,
    transparent 100%
  );
  backdrop-filter: blur(16px);
`;

const divisionChangeColor: Record<DivisionChange, string> = {
  [DivisionChange.PROMOTED]: 'var(--c-rgb-rivals-green)',
  [DivisionChange.RELEGATED]: 'var(--c-rgb-rivals-loss)',
  [DivisionChange.STABLE]: '69, 69, 69',
};

type Props = {
  so5LeaderboardContender?: LineupStatus_so5LeaderboardContender;
  message?: ReactNode;
  children?: ReactNode;
  className?: string;
  onClickSchedule?: () => void;
  onClickPrizePool?: () => void;
};

export const LineupStatus = ({
  so5LeaderboardContender,
  message: messageProp,
  className,
  onClickSchedule,
  onClickPrizePool,
  children,
}: Props) => {
  const isUpcoming =
    so5LeaderboardContender &&
    isFixtureOpened(so5LeaderboardContender.so5Leaderboard.so5Fixture);

  const { status, divisionChange } =
    useContenderStatus(so5LeaderboardContender || null) || {};

  if (!so5LeaderboardContender) {
    return <Root className={classNames(className, { isUpcoming })} />;
  }

  const { so5Leaderboard, so5Lineup } = so5LeaderboardContender;

  const ranking = so5Lineup?.so5Rankings.at(0);
  const { targetedRewardWithEligibility } = ranking || {};

  let content: ReactNode = null;
  let icon: ReactNode = null;
  let message: ReactNode = null;
  let caption: ReactNode = null;
  let color: string = 'var(--c-rgb-black)';
  let onClick: (() => void) | undefined;

  if (status === ContenderStatus.INCOMPLETE) {
    icon = (
      <FontAwesomeIcon icon={faExclamationCircle} color="var(--c-yellow-400)" />
    );
    message = (
      <FormattedMessage
        id="LineupStatus.incomplete"
        defaultMessage="Incomplete"
      />
    );
    color = '240, 206, 29';
  } else if (status === ContenderStatus.DIVISION_CHANGE && divisionChange) {
    color = divisionChangeColor[divisionChange];
    icon = <DumbStatus divisionChange={divisionChange} />;

    const statusMessage = isUpcoming
      ? messages.projectedToDiv
      : messages[divisionChange];
    const projectedDivisionDelta =
      divisionChange === DivisionChange.PROMOTED ? -1 : +1;
    const projectedDivision =
      (so5Leaderboard.division || 0) +
      (divisionChange === DivisionChange.STABLE ? 0 : projectedDivisionDelta);

    message = (
      <FormattedMessage
        {...statusMessage}
        values={{ division: projectedDivision }}
      />
    );
    onClick = onClickPrizePool;
  } else if (
    status === ContenderStatus.TARGET &&
    targetedRewardWithEligibility?.reward.target
  ) {
    if (targetedRewardWithEligibility.eligible) {
      color = getRgbColorFromHex(
        targetedRewardWithEligibility.reward.target.color
      );
      icon = (
        <img
          src={targetedRewardWithEligibility.reward.target.pictureUrl}
          alt=""
          width={16}
          height={16}
        />
      );
      message = (
        <FormattedMessage
          id="LineupStatus.targetReached"
          defaultMessage="Target Reached"
        />
      );
    } else if (
      so5Lineup?.so5Appearances.every(appearance => !appearance.upcomingGame)
    ) {
      icon = <FontAwesomeIcon icon={faTimes} color="var(--c-red-600)" />;
      message = (
        <FormattedMessage
          id="LineupStatus.targetMissed"
          defaultMessage="Target Missed"
        />
      );
    } else {
      icon = <FontAwesomeIcon icon={faHourglass} />;
      message = (
        <FormattedMessage
          id="LineupStatus.inProgress"
          defaultMessage="In Progress"
        />
      );
    }
    onClick = onClickPrizePool;
  } else if (status === ContenderStatus.THRESHOLD) {
    const thresholdColors = ['51, 206, 120', '112, 219, 198', '194, 219, 46'];

    const currentThresholdIndex = findLastIndex(
      so5Leaderboard.rewardsConfig?.conditional || [],
      reward => {
        if (!reward?.score || !ranking?.score) return false;
        return reward.score <= ranking.score;
      }
    );

    if (currentThresholdIndex >= 0) {
      color = thresholdColors[currentThresholdIndex];
      icon = (
        <TargetIcon
          color={`rgb(${thresholdColors[currentThresholdIndex]})` as HexColor}
        />
      );
      message = (
        <FormattedMessage
          id="LineupStatus.thresholdReached"
          defaultMessage="Target {targetNb}"
          values={{ targetNb: currentThresholdIndex + 1 }}
        />
      );
      caption = currentThresholdIndex > 0 && (
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage
            id="LineupStatus.nextThreshold"
            defaultMessage="<span>{points, number}pts</span> to next target"
            values={{
              points:
                (so5Leaderboard.rewardsConfig?.conditional?.[
                  currentThresholdIndex + 1
                ]?.score ?? 0) - (ranking?.score ?? 0),
              span: (...chunks) => (
                <span
                  style={{
                    color: 'var(--c-nl-100-opaque)',
                    fontWeight: 'bold',
                  }}
                >
                  {chunks}
                </span>
              ),
            }}
          />
        </LabelM>
      );
    } else if (
      so5Lineup?.so5Appearances.every(appearance => !appearance.upcomingGame) &&
      currentThresholdIndex === -1
    ) {
      icon = <FontAwesomeIcon icon={faTimes} color="var(--c-red-600)" />;
      message = (
        <FormattedMessage
          id="LineupStatus.targetMissed"
          defaultMessage="Target Missed"
        />
      );
    } else {
      icon = <FontAwesomeIcon icon={faHourglass} />;
      message = (
        <FormattedMessage
          id="LineupStatus.inProgress"
          defaultMessage="In Progress"
        />
      );
    }
    onClick = onClickPrizePool;
  } else if (status === ContenderStatus.EMPTY) {
    const simpleRewards = rewardsOverviewToSimpleRewards({
      rewardsOverview: so5Leaderboard.totalRewards,
      rarity: so5Leaderboard.mainRarityType,
    });

    color = '42, 42, 42';
    content = (
      <SBHorizontal>
        <BodyM bold>
          <FormattedMessage {...fantasy.viewPrizePool} />
        </BodyM>
        <Horizontal>
          {simpleRewards.map((simpleReward, i) => (
            <RewardOverview
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              size={3}
              variant={Variant.IMAGE_ONLY}
              simpleReward={simpleReward}
            />
          ))}
        </Horizontal>
      </SBHorizontal>
    );
    onClick = onClickPrizePool;
  } else if (status === ContenderStatus.NOT_STARTED) {
    color = 'var(--c-rgb-brand-300)';
    icon = <Timer size={2} fill="var(--c-brand-300)" />;
    message = <FormattedMessage {...fantasy.viewSchedule} />;
    onClick = onClickSchedule;
  }

  return (
    <Root
      style={
        {
          '--rgb-gradient-color': color,
        } as CSSProperties
      }
      className={classNames(className, { isUpcoming })}
    >
      {children}
      {(content || message || messageProp) && (
        <ConditionalWrapper
          wrap={!!onClick}
          Wrapper={ButtonBase}
          props={{ onClick }}
        >
          {content || (
            <Vertical center gap={0.5}>
              <Horizontal center>
                {icon}
                <BodyM bold>{messageProp || message}</BodyM>
              </Horizontal>
              {caption}
            </Vertical>
          )}
        </ConditionalWrapper>
      )}
    </Root>
  );
};

LineupStatus.fragments = {
  so5LeaderboardContender: gql`
    fragment LineupStatus_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        id # used to check if the query is fully loaded
        slug
        division
        mainRarityType
        totalRewards {
          ...rewardsOverviewToSimpleRewards_rewardsOverview
        }
        rewardsConfig {
          conditional {
            score
          }
        }
        so5Fixture {
          slug
          ...isFixtureOpened_so5Fixture
        }
      }
      so5Lineup {
        id
        so5Appearances {
          id
          upcomingGame {
            id
          }
        }
        so5Rankings {
          id
          score
          targetedRewardWithEligibility {
            eligible
            reward {
              score
              target {
                slug
                pictureUrl
                color
              }
            }
          }
        }
      }
      ...useContenderStatus_so5LeaderboardContender
    }
    ${useContenderStatus.fragments.so5LeaderboardContender}
    ${isFixtureOpened.fragments.so5Fixture}
    ${rewardsOverviewToSimpleRewards.fragments.rewardsOverview}
  ` as TypedDocumentNode<LineupStatus_so5LeaderboardContender>,
};
